import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [maintenanceInfo, setMaintenanceInfo] = useState(null);

    // Check maintenance status
    const checkMaintenance = useCallback(async () => {
        try {
            const response = await fetch('/api/maintenance/status');
            const data = await response.json();
            setMaintenanceMode(data.maintenance_mode);
            if (data.maintenance_mode) {
                setMaintenanceInfo({
                    message: data.maintenance_message,
                    end_time: data.maintenance_end
                });
            } else {
                setMaintenanceInfo(null);
            }
            return data.maintenance_mode;
        } catch (err) {
            console.error('Error checking maintenance:', err);
            return false;
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            fetchUser();
        } else {
            setLoading(false);
        }
        // Check maintenance status on load
        checkMaintenance();
    }, [checkMaintenance]);

    const fetchUser = async () => {
        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
        } catch (error) {
            localStorage.removeItem('token');
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const login = async (cid, password, totp) => {
        const response = await api.post('/auth/login', { cid, password, totp });
        // ถ้าต้องยืนยัน 2FA ก่อน — ยังไม่มี token ให้ caller ขอรหัสจากผู้ใช้
        if (response.data.require2fa && !response.data.token) {
            return response.data;
        }
        localStorage.setItem('token', response.data.token);
        // ดึงข้อมูล user ที่ครบถ้วน (รวม permissions) หลัง login
        await fetchUser();
        return response.data;
    };

    // เข้าสู่ระบบด้วย Google (credential = ID token จากปุ่ม Google)
    const loginWithGoogle = async (credential, totp) => {
        const response = await api.post('/auth/google-login', { credential, totp });
        if (response.data.require2fa && !response.data.token) {
            return response.data;
        }
        localStorage.setItem('token', response.data.token);
        await fetchUser();
        return response.data;
    };

    // เข้าสู่ระบบด้วย Provider ID — หลัง callback จาก moph.id.th server จะพากลับมาที่ /login?pid_code=...
    // นำรหัสใช้ครั้งเดียวนั้นมาแลก token (ถ้าผู้ใช้เปิด 2FA จะได้ require2fa กลับมาก่อน)
    const loginWithProviderId = async (code, totp) => {
        const response = await api.post('/auth/providerid/exchange', { code, totp });
        if (response.data.require2fa && !response.data.token) {
            return response.data;
        }
        localStorage.setItem('token', response.data.token);
        await fetchUser();
        return response.data;
    };

    const logout = async () => {
        try {
            await api.post('/auth/logout');
        } catch (error) {
            // Ignore error
        }
        localStorage.removeItem('token');
        setUser(null);
    };

    const isAdmin = () => {
        return user?.role === 'admin' || user?.role_name === 'super_admin' || user?.role_name === 'admin';
    };

    // ตรวจสอบว่า user มี permission หรือไม่
    const hasPermission = (permission) => {
        if (!user?.permissions) return false;
        // admin มีสิทธิ์ทุกอย่าง
        if (user?.role === 'admin' || user?.role_name === 'super_admin') return true;
        return user.permissions.includes(permission);
    };

    // ตรวจสอบว่า user มี permission อย่างน้อย 1 อัน
    const hasAnyPermission = (permissions) => {
        if (!user?.permissions) return false;
        if (user?.role === 'admin' || user?.role_name === 'super_admin') return true;
        return permissions.some(p => user.permissions.includes(p));
    };

    // ตรวจสอบว่าสามารถเข้าถึงหน้าจัดการระบบได้หรือไม่
    // = เจ้าหน้าที่ทุก role ที่ไม่ใช่ user ทั่วไป
    const canAccessAdmin = () => {
        if (isAdmin()) return true;
        // ถูกกำหนด role ที่ไม่ใช่ 'user' ทั่วไป (เช่น การเงิน, hr, role ที่ตั้งเอง)
        if (user?.role_name && user.role_name !== 'user') return true;
        // หรือมี permission ในกลุ่มงานบริหารอย่างน้อย 1 อัน
        return hasAnyPermission([
            'request.view_all',
            'request.process',
            'request.approve',
            'account_request.manage',
            'position_request.manage',
            'users.view',
            'roles.view',
            'settings.view',
            'view_reports',
            'admin_access'
        ]);
    };

    const value = {
        user,
        loading,
        login,
        loginWithGoogle,
        loginWithProviderId,
        logout,
        isAdmin,
        hasPermission,
        hasAnyPermission,
        canAccessAdmin,
        fetchUser,
        maintenanceMode,
        maintenanceInfo,
        checkMaintenance
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
